import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiPhone, FiMail, FiMapPin, FiCheck, FiStar, FiUsers, FiCamera } from 'react-icons/fi';
import './About.css';

const VALUES = [
  { title: 'Authenticity', desc: 'We believe in real travel experiences — meeting locals, exploring hidden paths, and creating genuine memories.' },
  { title: 'Safety First', desc: 'Every trip is planned with your safety as the top priority. Experienced guides and reliable transportation always.' },
  { title: 'Sustainability', desc: 'We travel responsibly, supporting local communities and preserving the natural beauty of the destinations we visit.' },
  { title: 'Transparency', desc: 'No hidden costs, no surprises. Every rupee is accounted for with complete clarity before you book.' },
];

const STORY_MAIN_IMG  = '/kashmir.jpg';
const STORY_SMALL_IMG = '/kerala.jpg';

const GALLERY_IMGS = [
  { src: '/ladakh.jpg',    label: 'Ladakh' },
  { src: '/kashmir.jpg',   label: 'Kashmir' },
  { src: '/meghalaya.jpg', label: 'Meghalaya' },
  { src: '/kerala.jpg',    label: 'Kerala' },
  { src: '/srilanka.jpg',  label: 'Sri Lanka' },
];

const WHY_CARDS = [
  {
    img: '/ladakh.jpg',
    fallback: 'https://images.pexels.com/photos/4534200/pexels-photo-4534200.jpeg?auto=compress&cs=tinysrgb&w=500&h=380&fit=crop',
    title: 'Expert Local Guides',
    desc: 'Every trip is led by experienced local guides who know the terrain, culture, and hidden spots.',
  },
  {
    img: '/kashmir.jpg',
    fallback: 'https://images.pexels.com/photos/4534200/pexels-photo-4534200.jpeg?auto=compress&cs=tinysrgb&w=500&h=380&fit=crop',
    title: 'Personalized Journeys',
    desc: 'We tailor each trip to your pace, interests and budget. No two trips are ever the same.',
  },
  {
    img: 'https://images.pexels.com/photos/4534200/pexels-photo-4534200.jpeg?auto=compress&cs=tinysrgb&w=500&h=380&fit=crop',
    fallback: 'https://images.pexels.com/photos/2526935/pexels-photo-2526935.jpeg?auto=compress&cs=tinysrgb&w=500&h=380&fit=crop',
    title: 'Small Group Experiences',
    desc: 'Max 12-15 people per group for an intimate, meaningful, and safe travel experience.',
  },
];
export default function About() {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
  };

  return (
    <div className="about-page">

      {/* ── Hero ── */}
      <div className="about-hero">
        <div
          className="about-hero-bg"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1920&q=80)' }}
        />
        <div className="about-hero-overlay" />
        <motion.div
          className="about-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span style={{ fontFamily: 'var(--font-script)', fontSize: '1.3rem', color: 'var(--secondary-light)' }}>
            Our Story
          </span>
          <h1>About The Unexplored Routes</h1>
          <p>Born from a love of travel and a desire to show the world India's hidden gems</p>
        </motion.div>
      </div>

      {/* ── Story Section ── */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">

            {/* Left — images */}
            <motion.div className="story-images" {...fadeUp}>
              <img
                src={STORY_MAIN_IMG}
                alt="Mountain trekking adventure in India"
                className="story-img-main"
                onError={(e) => {
                  e.target.src = '/ladakh.jpg';
                }}
              />
              <motion.img
                src={STORY_SMALL_IMG}
                alt="Group of happy travelers"
                className="story-img-secondary"
                initial={{ opacity: 0, scale: 0.85, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                onError={(e) => {
                  e.target.src = '/ladakh.jpg';
                }}
              />
              {/* Floating badge */}
              <motion.div
                className="story-badge"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                animate={{ y: [0, -6, 0] }}
              >
                <FiStar className="story-badge-icon" />
                <div>
                  <strong>5+ Years</strong>
                  <span>of Offbeat Travel</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right — text */}
            <motion.div className="story-content" {...fadeUp}>
              <span className="section-subtitle">Our Journey</span>
              <h2 className="section-title">How We Started</h2>
              <p>
                The Unexplored Routes was founded with a single mission — to help travelers discover
                the incredible beauty of India's lesser-known destinations. As an offbeat travel agency
                based in Gwalior, Madhya Pradesh, we specialize in crafting journeys that go far beyond
                the typical tourist trail.
              </p>
              <p>
                Our founder Manik is a passionate traveler who fell in love with India's remote landscapes
                and diverse cultures. After years of exploring places like Ladakh, Meghalaya, Arunachal
                Pradesh, and Spiti Valley, he decided to share these experiences with others — creating
                memorable trips that combine adventure, culture, and authentic local experiences.
              </p>
              <p>
                Today, we have guided 500+ happy travelers across India and Sri Lanka, earning a
                reputation for excellent planning, personal attention, and genuine care for every
                traveler in our group.
              </p>
              <div className="story-facts">
                <div className="fact"><strong>500+</strong><span>Happy Travelers</span></div>
                <div className="fact"><strong>50+</strong><span>Destinations</span></div>
                <div className="fact"><strong>5+</strong><span>Years Experience</span></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Photo Gallery Strip ── */}
      <section className="about-gallery-section">
        <div className="container">
          <motion.div style={{ textAlign: 'center', marginBottom: '40px' }} {...fadeUp}>
            <span className="section-subtitle">Through Our Lens</span>
            <h2 className="section-title">Moments From Our Journeys</h2>
          </motion.div>
          <div className="about-gallery-grid">
            {GALLERY_IMGS.map((img, i) => (
              <motion.div
                key={img.label}
                className={`gallery-item ${i === 0 ? 'tall' : ''}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.55 }}
                whileHover={{ scale: 1.03 }}
              >
                <img
                  src={img.src}
                  alt={img.label}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/ladakh.jpg';
                  }}
                />
                <div className="gallery-item-overlay" />
                <div className="gallery-item-label">
                  <FiCamera /> {img.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Destinations We Cover ── */}
      <section className="destinations-cover">
        <div className="container">
          <motion.div style={{ textAlign: 'center', marginBottom: '48px' }} {...fadeUp}>
            <span className="section-subtitle">Upcoming Trips</span>
            <h2 className="section-title">Where We Go</h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              Our carefully selected destinations offer the perfect blend of adventure, culture, and natural beauty.
            </p>
          </motion.div>
          <motion.div
            className="destinations-list"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {['Ladakh', 'Kashmir', 'Meghalaya', 'Kerala', 'Spiti Valley', 'Sri Lanka', 'Arunachal Pradesh', 'Coorg'].map((dest) => (
              <motion.span
                key={dest}
                className="dest-tag"
                whileHover={{ scale: 1.06, backgroundColor: 'var(--primary)', color: 'white' }}
                transition={{ duration: 0.2 }}
              >
                <FiCheck className="dest-check" /> {dest}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="values-section">
        <div className="container">
          <motion.div style={{ textAlign: 'center', marginBottom: '56px' }} {...fadeUp}>
            <span className="section-subtitle">What We Stand For</span>
            <h2 className="section-title">Our Core Values</h2>
          </motion.div>
          <div className="values-grid">
            {VALUES.map((val, i) => (
              <motion.div
                key={val.title}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -6 }}
              >
                <div className="value-number">0{i + 1}</div>
                <h3>{val.title}</h3>
                <p>{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Travel With Us ── */}
      <section className="about-why-section">
        <div className="container">
          <motion.div style={{ textAlign: 'center', marginBottom: '56px' }} {...fadeUp}>
            <span className="section-subtitle">Our Strengths</span>
            <h2 className="section-title">Why 500+ Travelers Choose Us</h2>
          </motion.div>
          <div className="about-why-grid">
            {WHY_CARDS.map((item, i) => (
              <motion.div
                key={item.title}
                className="about-why-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
              >
                <div className="about-why-img">
                  <img
                    src={item.img}
                    alt={item.title}
                    onError={(e) => { e.target.src = item.fallback; }}
                  />
                  <div className="about-why-img-overlay" />
                </div>
                <div className="about-why-body">
                  <div className="about-why-num">0{i + 1}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact Section ── */}
      <section className="about-contact">
        <div className="container">
          <motion.div className="about-contact-grid" {...fadeUp}>
            <div className="about-contact-content">
              <span className="section-subtitle">Get In Touch</span>
              <h2 className="section-title">Let's Plan Your Adventure</h2>
              <p>Ready to explore India's hidden gems? Reach out and let's craft the perfect journey for you.</p>
              <div className="about-contact-stats">
                <div><FiUsers /><strong>500+</strong><span>Happy Travelers</span></div>
                <div><FiStar /><strong>4.9★</strong><span>Avg Rating</span></div>
              </div>
              <Link to="/contact" className="btn-primary" style={{ marginTop: '24px' }}>
                Contact Us <FiArrowRight />
              </Link>
            </div>
            <div className="about-contact-info">
              <div className="info-item">
                <div className="info-icon"><FiPhone /></div>
                <div>
                  <strong>Phone</strong>
                  <a href="tel:+918109849232">+91 81098 49232</a>
                  <a href="tel:+919425122751">+91 94251 22751</a>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiMail /></div>
                <div>
                  <strong>Email</strong>
                  <a href="mailto:hello@theunexploredroutes.com">hello@theunexploredroutes.com</a>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiMapPin /></div>
                <div>
                  <strong>Address</strong>
                  <span>58/2, Ashok Niwas, Daulat Gang, Gwalior, MP - 474001</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
